import { Box, Container, Typography, Stack } from "@mui/material"
import React, { useRef, useLayoutEffect } from "react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useIsMobile } from '@/hooks/Responsive'
import Visual from '@assets/movie/intro.mp4'
import mainimg from '@assets/images/sub/img_intro.png'
import mainimg_m from '@assets/images/sub/img_intro_m.png'
import img1 from '@assets/images/sub/img_intro_1.png'
import img2 from '@assets/images/sub/img_intro_2.png'
import img3 from '@assets/images/sub/img_intro_3.png'
import Bg from '@assets/images/sub/bg_banner.png'
import { useLang } from '@/i18n/useLang'

gsap.registerPlugin(ScrollTrigger)

export default function Intro() {
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const visualRef = useRef(null)

  // useLayoutEffect(() => {
  //   if (isMobile) return

  //   let isExpanded = false

  //   const handleScroll = () => {
  //     if (!visualRef.current) return

  //     const scrollY = window.scrollY

  //     if (scrollY <= 100 && isExpanded) {
  //       isExpanded = false
  //       gsap.to(visualRef.current, {
  //         height: 280,
  //         duration: 2,
  //         ease: 'power2.out',
  //       })
  //     }

  //     if (scrollY > 100 && !isExpanded) {
  //       isExpanded = true
  //       gsap.to(visualRef.current, {
  //         height: 528,
  //         duration: 2,
  //         ease: 'power2.out',
  //       })
  //     }
  //   }

  //   window.addEventListener('scroll', handleScroll)
  //   return () => window.removeEventListener('scroll', handleScroll)
  // }, [isMobile, lang])

  const containerRef = useRef(null)
  const linesRef = useRef([])
  const linesPC = [
    "대학, 기업, 개발자들이 보유한",
    "XR(VR/AR) 학습 콘텐츠를 공유하고,",
    "협업할 수 있도록 돕습니다."
  ];

  const linesMobile = [
    "대학, 기업, 개발자들이",
    "보유한 XR(VR/AR)",
    "학습 콘텐츠를 공유하고,",
    "협업할 수 있도록 돕습니다."
  ];

  const lines = isMobile ? linesMobile : linesPC;

  useLayoutEffect(() => {
    if (isMobile) {
      gsap.set(linesRef.current, {
        clearProps: 'all',
        opacity: 1,
        y: 0,
        pointerEvents: 'auto',
      })
      return
    }

    const ctx = gsap.context(() => {
      gsap.set(linesRef.current, {
        y: 50,
        opacity: 0,
        pointerEvents: 'none',
      })

      gsap.to(linesRef.current, {
        y: 0,
        opacity: 1,
        pointerEvents: 'auto',
        stagger: 0.5,
        duration: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 80%',
          end: 'top 35%',
          scrub: 1.2,
        },
      })
    })

    return () => ctx.revert()
  }, [isMobile, lang, lines])

  const explain = [
    { title: '학생 및 일반인', description: '고등, 평생, 직업교육기관의 학생들은 물론, 일반인들도 다양하고 풍부한 교육 콘텐츠에 무료로 접근할 수 있습니다. 고위험·고비용·특수환경 실험실습 및 실감형 기술이 적용될 수 있는 다양한 분야의 콘텐츠를 통해 효과적인 학습을 경험하며 지식 습득과 역량 강화에 도움을 받을 수 있습니다.', image: img1 },
    { title: '교육기관', description: '각 교육기관은 보유한 XR 콘텐츠를 플랫폼에 탑재하여 기관간에 서로 공유하고, 기관의 LMS와 연동하여 교육에 활용할 수 있습니다. 이 를 통해 각 기관은 콘텐츠 제작 부담을 줄이고 콘텐츠를 활용하여 교육의 효과를 높일 수 있습니다.', image: img2 },
    { title: '개발사', description: '보유한 콘텐츠를 플랫폼에 공유함으로써 사회공헌 활동을 펼치거나, 산학 협력 기회를 확대할 수 있습니다. 또한, 직무 교육이나 직원 훈련에 필요한 고비용, 고위험의 가상 콘텐츠를 활용하여 직업 교육의 효율성을 높일 수 있습니다.', image: img3 },
  ]

  const bannerRef = useRef(null)

  useLayoutEffect(() => {
    if (isMobile) return

    const ctx = gsap.context(() => {
      gsap.set(bannerRef.current, {
        y: 0,
        opacity: 1,
        pointerEvents: 'none',
      })

      gsap.to(bannerRef.current, {
        y: 0,
        opacity: 1,
        pointerEvents: 'auto',
        ease: 'power2.out',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top 85%',
          end: 'top 40%',
          scrub: 1.2,
        },
      })
    })

    return () => ctx.revert()
  }, [isMobile, lang])

  return (
    <Box>
      <Container maxWidth="xl" disableGutters>
        {lang === 'ko' && (
          <Typography color="primary.main" fontWeight={700} sx={{ mb: 2 }}>
            The Core Hub for Immersive Education
          </Typography>
        )}

        <Stack
          direction={isMobile || lang === 'en' ? 'column' : 'row'}
          justifyContent="space-between"
          sx={{ mb: 8 }}
        >
          <Typography variant={isMobile ? 'h3' : 'h1'} sx={{ letterSpacing: '-0.04em', lineHeight: '1.3' }}>
            실감형 교육의
            <Box component="span" sx={{ display: 'block' }}>
              중심 허브
            </Box>
          </Typography>

          <Box sx={{ py: 2 }}>
            <Typography
              component="p"
              variant={isMobile ? 'subtitle3' : 'subtitle2'}
              fontWeight={400}
              sx={{ mb: 1.5 }}
            >
              <Box
                component="strong"
                sx={{ fontWeight: lang === 'ko' ? 600 : 400 }}
              >
                OpenVLab은 한국방송통신대학교가 교육부의 'AID 30+ 프로젝트'
              </Box>

              {!isMobile && lang === 'ko' && (
                <Box component="span" sx={{ display: 'block' }} />
              )}

              의 일환으로 구축하는 개방형 공공 학습 플랫폼입니다.
            </Typography>

            <Typography
              component="p"
              variant={isMobile ? 'subtitle3' : 'subtitle2'}
              fontWeight={400}
            >
              기관과 기술, 학습자, 데이터를 유기적으로 연결하여 실감형 교육의

              {lang === 'ko' && (
                <Box component="span" sx={{ display: 'block' }} />
              )}

              중심 허브가 되는 것을 목표로 합니다.
            </Typography>
          </Box>
        </Stack>
        {/* <Box
          component="video"
          ref={visualRef}
          src={Visual}
          autoPlay
          muted
          loop
          playsInline
          aria-label="OpenVLab 실감형 교육의 중심 허브 영상"
          aria-describedby="intro-video-desc"
          sx={{
            width: '100%',
            height: isMobile ? 'auto' : '280px',
            objectFit: 'fill',
            display: 'block',
            borderRadius: 4,
            overflow: 'hidden',
            ...(isMobile && {
              aspectRatio: 3 / 5
            })
          }}
        /> */}
        <Box
          component="img"
          src={isMobile ? mainimg_m : mainimg}
          alt="VR 헤드셋을 착용한 사용자가 가상 지구 홀로그램을 손으로 조작하며 실감형 기술을 체험하는 모습"
          sx={{ borderRadius: 4, width: '100%' }}
        />
      </Container>
      <Container disableGutters 
        sx={{
          minHeight: isMobile ? 'auto' : 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 20,
        }}
      >
        <Stack alignItems="center" ref={containerRef}>
          {lines.map((text, i) => (
            <Typography
              key={i}
              component="p"
              variant={isMobile || lang === 'en' ? 'h5' : 'h4'}
              fontWeight={isMobile ? '600' : '700'}
              ref={(el) => { if (el) linesRef.current[i] = el }}
              sx={{ 
                opacity: isMobile ? 1 : 0, 
                mb: isMobile ? 0 : 2,
                ...(isMobile && {
                  textAlign: 'center',
                  lineHeight: '1.8'
                })
              }}
            >
              {text}
            </Typography>
          ))}
        </Stack>
      </Container>
      <Container disableGutters sx={{ pb: isMobile ? 10 : 17 }}>
        <Stack sx={{ gap: isMobile ? 8 : 6 }}>
          {explain.map((item, i) => (
            <Stack key={i} flexDirection={isMobile ? 'column' : 'row'} sx={{ gap: isMobile ? 1.5 : 6 }}>
              <Box
                sx={{
                  width: isMobile ? '100%' : '560px',
                  aspectRatio: '7 / 4',
                  borderRadius: 4,
                  overflow: 'hidden',
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography component="p" variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={700} sx={{ my: 1.5 }}>
                  {item.title}
                </Typography>
                <Typography>
                  {item.description}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Container>
      <Container disableGutters>
        <Box
          ref={bannerRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: isMobile ? '144px' : '200px',
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            opacity: isMobile ? 1 : 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${Bg})`,
              backgroundSize: '100% 100%',
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: -1,
            }}
          />
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} component="p" fontWeight={700} color="text.contrastText" sx={{ mb: isMobile ? 0 : 1.5, ...(isMobile && { textAlign: 'center' }) }}>연결이 만드는 새로운 서비스</Typography>
          <Typography fontWeight={500} color="text.contrastText" sx={{ display: isMobile ? 'none' : 'block' }}>표준 API로 서비스와 데이터를 간편하게 이용해 주세요.</Typography>
        </Box>
      </Container>
    </Box>
  )
}
